import { IconType } from "react-icons";
import { IconBaseProps } from "react-icons/lib";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  FaTachometerAlt,
  FaUser,
  FaCalendarAlt,
  FaMoneyBill,
  FaTooth,
  // FaAllergies, // hidden on client request
  // FaNotesMedical, // hidden on client request
  FaClipboardList,
  FaUserInjured,
  FaPrescriptionBottle,
  FaBookOpen,
  FaFileMedical,
  FaStream,
  FaFileAlt,
} from "react-icons/fa";

interface MenuItem {
  name: string;
  icon: IconType;
  path: string;
  roles: string[]; // Add roles field to control access
}

const allMenuItems: MenuItem[] = [
  { name: "Users", icon: FaUser, path: "/admin", roles: ["ADMIN"] }, // Only ADMIN can see
  { name: "Dashboard", icon: FaTachometerAlt, path: "/dashboard", roles: ["DOCTOR"] }, // Only DOCTOR can see
  { name: "Appointments", icon: FaCalendarAlt, path: "/appointments", roles: ["DOCTOR"] },
  { name: "Clinics", icon: FaClipboardList, path: "/clinics", roles: ["DOCTOR"] },
  { name: "Patients", icon: FaUserInjured, path: "/patients", roles: ["DOCTOR"] },
  { name: "Treatment", icon: FaTooth, path: "/treatments", roles: ["DOCTOR"] },
  { name: "Treatment Plan", icon: FaClipboardList, path: "/treatment-plan", roles: ["DOCTOR"] },
  { name: "Treatment Record", icon: FaFileMedical, path: "/treatment-records", roles: ["DOCTOR"] },
  { name: "Educational Hub", icon: FaBookOpen, path: "/educational-resources", roles: ["DOCTOR"] },
  // Hidden on client request. Uncomment to restore.
  // { name: "Allergy", icon: FaAllergies, path: "/allergy", roles: ["DOCTOR"] },
  // { name: "Medical Conditions", icon: FaNotesMedical, path: "/medical", roles: ["DOCTOR"] },
  { name: "Patient Timeline", icon: FaStream, path: "/patient-timeline", roles: ["DOCTOR"] },
  { name: "Payments", icon: FaMoneyBill, path: "/payments", roles: ["DOCTOR"] },
];

export default function Sidebar({
  isOpen,
  toggle,
}: {
  isOpen: boolean;
  toggle: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState<string>("");

  // Get user role from localStorage
  useEffect(() => {
    const role = localStorage.getItem("userRole") || "";
    setUserRole(role);
  }, []);

  // Filter menu items based on user role
  const menuItems = allMenuItems.filter(item => 
    item.roles.includes(userRole)
  );
  return (
    <div
      className={`bg-white shadow-md h-screen min-h-0 flex flex-col transition-all duration-500
        ${isOpen ? "w-60" : "w-13"}`}
    >
      {/* Header: Toggle + Project Name */}

      <div className="flex items-center p-4 border-b border-gray-200 shadow-md">
        <button
          className="text-gray-700 text-2xl focus:outline-none mr-3 hover:text-blue-500"
          onClick={toggle}
        >
          ☰
        </button>

        {isOpen && (
          <span className="text-xl font-bold text-blue-600 select-none">
            Orthodontic
          </span>
        )}
      </div>

      {/* Menu Items */}
      <ul className="mt-5 ml-1 flex-1 overflow-y-auto no-scrollbar pr-2">
        {menuItems.map((item, idx) => {
          const Icon = item.icon as React.ComponentType<IconBaseProps>;
          const currentPath = location.pathname;
          const isActive =
            currentPath === item.path ||
            currentPath.startsWith(`${item.path}/`) ||
            // Ensure create/edit routes still highlight main menu
            (item.path === "/treatments" &&
              (currentPath === "/treatment" ||
                currentPath.startsWith("/treatment/"))) ||
            (item.path === "/treatment-records" &&
              currentPath.startsWith("/treatment-record/")) ||
            (item.path === "/payments" &&
              (currentPath === "/payment" ||
                currentPath.startsWith("/payment/")));
          return (
            <li
              key={idx}
              className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${isActive
                ? "bg-blue-100 text-blue-700 border border-blue-200 shadow-sm"
                : "hover:bg-blue-50 text-gray-700"
                }`}
              onClick={() => navigate(item.path)}
              aria-current={isActive ? "page" : undefined}
            >
              <Icon
                className={`text-xl ${isActive ? "text-blue-600" : "text-blue-500"
                  }`}
              />
              {isOpen && (
                <span
                  className={`font-medium ${isActive ? "text-blue-700" : "text-gray-700"
                    }`}
                >
                  {item.name}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
